import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, RefreshCw, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database';

type KipRow = Database['public']['Tables']['kips']['Row'];
type AgentRow = Database['public']['Tables']['agents']['Row'];
type KipCategory = KipRow['category'];
type KipAvailability = KipRow['availability'];

const CATEGORIES: KipCategory[] = ['Communication', 'Empathy', 'Accuracy', 'Education', 'Progress', 'Collaboration'];

const categoryColors: Record<KipCategory, string> = {
  Communication: 'bg-blue-50 text-blue-700',
  Empathy: 'bg-purple-50 text-purple-700',
  Accuracy: 'bg-green-50 text-green-700',
  Education: 'bg-yellow-50 text-yellow-700',
  Progress: 'bg-orange-50 text-orange-700',
  Collaboration: 'bg-pink-50 text-pink-700',
};

const defaultKip = (): Omit<KipRow, 'created_at' | 'updated_at'> => ({
  id: '',
  title: '',
  description: '',
  category: 'Communication',
  duration: '15 min',
  client_id: null,
  guardian_id: null,
  colleague_id: null,
  agent_id: null,
  rubric: {},
  is_active: true,
  sort_order: 0,
  availability: 'coming_soon',
});

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
}> = ({ label, value, onChange, multiline }) => (
  <div>
    <label className="block text-xs font-medium text-[#555555] mb-1">{label}</label>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] resize-none"
      />
    ) : (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A]"
      />
    )}
  </div>
);

const KipForm: React.FC<{
  initial: Omit<KipRow, 'created_at' | 'updated_at'>;
  agents: AgentRow[];
  onSave: (data: Omit<KipRow, 'created_at' | 'updated_at'>) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}> = ({ initial, agents, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="border border-[#DADADA] rounded-xl p-4 bg-[#FAFAFA] space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="ID (slug)" value={form.id} onChange={(v) => set('id', v)} />
        <Field label="Title" value={form.title} onChange={(v) => set('title', v)} />
        <div>
          <label className="block text-xs font-medium text-[#555555] mb-1">Category</label>
          <select
            value={form.category}
            onChange={(e) => set('category', e.target.value as KipCategory)}
            className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] bg-white"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <Field label="Duration" value={form.duration} onChange={(v) => set('duration', v)} />
        <div>
          <label className="block text-xs font-medium text-[#555555] mb-1">Learner catalog</label>
          <select
            value={form.availability}
            onChange={(e) => set('availability', e.target.value as KipAvailability)}
            className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] bg-white"
          >
            <option value="available">Available</option>
            <option value="coming_soon">Coming soon</option>
            <option value="draft">Draft (hidden from learners)</option>
          </select>
        </div>
        <Field label="Sort Order" value={String(form.sort_order)} onChange={(v) => set('sort_order', Number(v))} />
        <div className="flex items-center gap-2 pt-5">
          <input
            type="checkbox"
            id="is_active"
            checked={form.is_active}
            onChange={(e) => set('is_active', e.target.checked)}
            className="rounded"
          />
          <label htmlFor="is_active" className="text-sm text-[#555555]">Active (visible to users)</label>
        </div>
      </div>
      <Field label="Description" value={form.description} onChange={(v) => set('description', v)} multiline />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Client ID" value={form.client_id ?? ''} onChange={(v) => set('client_id', v || null)} />
        <Field label="Guardian ID" value={form.guardian_id ?? ''} onChange={(v) => set('guardian_id', v || null)} />
        <Field label="Colleague ID" value={form.colleague_id ?? ''} onChange={(v) => set('colleague_id', v || null)} />
        <div>
          <label className="block text-xs font-medium text-[#555555] mb-1">Agent</label>
          <select
            value={form.agent_id ?? ''}
            onChange={(e) => set('agent_id', e.target.value || null)}
            className="w-full border border-[#DADADA] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1A1A1A] bg-white"
          >
            <option value="">None</option>
            {agents.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name} ({agent.id})
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => void onSave(form)}
          disabled={saving}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333] disabled:opacity-50"
        >
          <Check size={14} />
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-1.5 border border-[#DADADA] text-sm rounded-lg hover:bg-[#F5F5F5] text-[#555555]"
        >
          <X size={14} />
          Cancel
        </button>
      </div>
    </div>
  );
};

const KipsAdmin: React.FC = () => {
  const [rows, setRows] = useState<KipRow[]>([]);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState<KipCategory | 'all'>('all');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterAgent, setFilterAgent] = useState<'all' | 'no_agent'>('all');

  const fetch = useCallback(async () => {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    // Admins need to see all kips (including inactive), hence no is_active filter here
    const { data, error: e } = await supabase
      .from('kips')
      .select('*')
      .order('sort_order')
      .order('title');
    const { data: agentsData, error: agentsError } = await supabase
      .from('agents')
      .select('*')
      .order('name');
    if (e || agentsError) setError(e?.message ?? agentsError?.message ?? 'Unable to load admin data');
    else {
      setRows(data ?? []);
      setAgents(agentsData ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void fetch(); }, [fetch]);

  const saveNew = async (form: Omit<KipRow, 'created_at' | 'updated_at'>) => {
    if (!supabase) return;
    setSaving(true);
    const { error: e } = await supabase.from('kips').insert(form);
    if (e) { setError(e.message); setSaving(false); return; }
    setAdding(false); setSaving(false); void fetch();
  };

  const saveEdit = async (form: Omit<KipRow, 'created_at' | 'updated_at'>) => {
    if (!supabase) return;
    setSaving(true);
    const { error: e } = await supabase.from('kips').update(form).eq('id', form.id);
    if (e) { setError(e.message); setSaving(false); return; }
    setEditing(null); setSaving(false); void fetch();
  };

  const remove = async (id: string) => {
    if (!supabase || !confirm('Delete this kip? Sessions referencing it will be blocked.')) return;
    const { error: e } = await supabase.from('kips').delete().eq('id', id);
    if (e) setError(e.message);
    else void fetch();
  };

  const toggleActive = async (kip: KipRow) => {
    if (!supabase) return;
    const { error: e } = await supabase.from('kips').update({ is_active: !kip.is_active }).eq('id', kip.id);
    if (e) setError(e.message);
    else setRows((prev) => prev.map((r) => (r.id === kip.id ? { ...r, is_active: !r.is_active } : r)));
  };

  const shiftOrder = async (kip: KipRow, direction: 'up' | 'down') => {
    if (!supabase) return;
    const newOrder = direction === 'up' ? kip.sort_order - 1 : kip.sort_order + 1;
    const { error: e } = await supabase.from('kips').update({ sort_order: newOrder }).eq('id', kip.id);
    if (e) setError(e.message);
    else void fetch();
  };

  const filtered = rows.filter((r) => {
    if (filterCategory !== 'all' && r.category !== filterCategory) return false;
    if (filterActive === 'active' && !r.is_active) return false;
    if (filterActive === 'inactive' && r.is_active) return false;
    if (filterAgent === 'no_agent' && r.agent_id != null) return false;
    return true;
  });

  if (loading) return (
    <div className="flex justify-center py-8">
      <div className="w-5 h-5 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value as KipCategory | 'all')}
          className="border border-[#DADADA] rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#1A1A1A]"
        >
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>

        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value as typeof filterActive)}
          className="border border-[#DADADA] rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#1A1A1A]"
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>

        <select
          value={filterAgent}
          onChange={(e) => setFilterAgent(e.target.value as typeof filterAgent)}
          className="border border-[#DADADA] rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#1A1A1A]"
        >
          <option value="all">All assignments</option>
          <option value="no_agent">No agent assigned</option>
        </select>

        <span className="text-sm text-[#888888] ml-auto">{filtered.length} of {rows.length} kips</span>

        <button
          onClick={() => void fetch()}
          className="flex items-center gap-1 text-sm text-[#555555] hover:text-[#1A1A1A]"
        >
          <RefreshCw size={13} /> Refresh
        </button>

        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-sm rounded-lg hover:bg-[#333333]"
        >
          <Plus size={14} /> Add Kip
        </button>
      </div>

      {adding && (
        <KipForm
          initial={defaultKip()}
          agents={agents}
          onSave={saveNew}
          onCancel={() => setAdding(false)}
          saving={saving}
        />
      )}

      {/* Table */}
      <div className="bg-white border border-[#DADADA] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#DADADA] bg-[#FAFAFA]">
              <th className="text-left px-4 py-3 font-medium text-[#555555]">Order</th>
              <th className="text-left px-4 py-3 font-medium text-[#555555]">Title</th>
              <th className="text-left px-4 py-3 font-medium text-[#555555]">Category</th>
              <th className="text-left px-4 py-3 font-medium text-[#555555]">Catalog</th>
              <th className="text-left px-4 py-3 font-medium text-[#555555]">Duration</th>
              <th className="text-center px-4 py-3 font-medium text-[#555555]">Active</th>
              <th className="text-right px-4 py-3 font-medium text-[#555555]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#DADADA]">
            {filtered.map((kip) => (
              <React.Fragment key={kip.id}>
                <tr className={`hover:bg-[#FAFAFA] transition-colors ${!kip.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => void shiftOrder(kip, 'up')}
                        className="text-[#DADADA] hover:text-[#555555]"
                        aria-label="Move up"
                      >
                        <ArrowUp size={13} />
                      </button>
                      <span className="text-center text-xs text-[#888888]">{kip.sort_order}</span>
                      <button
                        onClick={() => void shiftOrder(kip, 'down')}
                        className="text-[#DADADA] hover:text-[#555555]"
                        aria-label="Move down"
                      >
                        <ArrowDown size={13} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1A1A1A]">{kip.title}</div>
                    <div className="text-xs text-[#888888] font-mono">{kip.id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${categoryColors[kip.category]}`}>
                      {kip.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-[#555555] capitalize">{kip.availability.replace('_', ' ')}</span>
                  </td>
                  <td className="px-4 py-3 text-[#555555]">{kip.duration}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => void toggleActive(kip)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        kip.is_active
                          ? 'bg-green-50 text-green-700 hover:bg-green-100'
                          : 'bg-[#F5F5F5] text-[#888888] hover:bg-[#EBEBEB]'
                      }`}
                    >
                      {kip.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                      {kip.is_active ? 'Active' : 'Hidden'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditing(editing === kip.id ? null : kip.id)}
                        className="text-[#888888] hover:text-[#1A1A1A]"
                        aria-label="Edit kip"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => void remove(kip.id)}
                        className="text-[#888888] hover:text-red-500"
                        aria-label="Delete kip"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
                {editing === kip.id && (
                  <tr>
                    <td colSpan={7} className="px-4 py-3 border-t border-[#DADADA] bg-[#FAFAFA]">
                      <KipForm
                        initial={{
                          id: kip.id, title: kip.title, description: kip.description,
                          category: kip.category, duration: kip.duration,
                          client_id: kip.client_id, guardian_id: kip.guardian_id,
                          colleague_id: kip.colleague_id, agent_id: kip.agent_id,
                          rubric: kip.rubric, is_active: kip.is_active, sort_order: kip.sort_order,
                          availability: kip.availability,
                        }}
                        agents={agents}
                        onSave={saveEdit}
                        onCancel={() => setEditing(null)}
                        saving={saving}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-[#888888]">No kips match the current filters.</div>
        )}
      </div>
    </div>
  );
};

export default KipsAdmin;
